import { Copy } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/shared/lib/cn";
import { formatNumber } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { TempBadge } from "@/widgets/TempBadge";

type Ideas = Record<string, unknown>;
type Item = {
  cluster_id: string;
  main_phrase: string;
  intent: string;
  temperature: string;
  trend_pct: number | null;
  total_count: number;
  next_peak_month: number | null;
  top_phrases: string[];
  ideas: Ideas | null;
  priority?: string;
};

const MONTHS = [
  "", "январь", "февраль", "март", "апрель", "май", "июнь",
  "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
];

export interface IdeaCardProps {
  item: Item;
  type: "video" | "posts" | "lead_magnets" | "full";
}

export function IdeaCard({ item, type }: IdeaCardProps): JSX.Element {
  return (
    <div className="glass-card p-5">
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <h3 className="text-base font-bold text-white">{item.main_phrase}</h3>
        <span className="font-mono text-sm text-cyan">
          {formatNumber(item.total_count)}
        </span>
        <TempBadge
          heat={{
            temperature: item.temperature,
            trend_pct: item.trend_pct,
          }}
        />
        {item.priority && (
          <span
            className={cn(
              "rounded-md border px-2 py-0.5 text-[11px] font-bold",
              item.priority === "P0"
                ? "border-pink/45 bg-pink/15 text-pink"
                : item.priority === "P1"
                  ? "border-amber/35 bg-amber/12 text-amber"
                  : "border-white/15 bg-white/[0.05] text-muted-foreground",
            )}
          >
            {item.priority}
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 text-muted-foreground hover:text-white"
          onClick={() => {
            void navigator.clipboard.writeText(item.main_phrase);
            toast.success("Скопировано");
          }}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>

      {item.ideas ? (
        <IdeasBody ideas={item.ideas} type={type} />
      ) : (
        <div className="text-xs italic text-muted-foreground">
          Идеи ещё не сгенерированы — статус анализа `ready`.
        </div>
      )}

      {item.top_phrases.length > 0 && (
        <div className="mt-4 border-t border-white/[0.08] pt-3 text-xs">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Топ фраз
          </div>
          <ol className="list-decimal pl-5 text-foreground/85">
            {item.top_phrases.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function IdeasBody({
  ideas,
  type,
}: {
  ideas: Ideas;
  type: IdeaCardProps["type"];
}): JSX.Element {
  if (type === "video") return <VideoIdeas ideas={ideas} />;
  if (type === "posts") return <PostsIdeas ideas={ideas} />;
  if (type === "lead_magnets") return <LeadIdeas ideas={ideas} />;
  return <FullIdeas ideas={ideas} />;
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
      <div className="min-w-[120px] text-[10px] font-bold uppercase tracking-wider text-muted-foreground sm:pt-0.5">
        {label}
      </div>
      <div className="flex-1 text-sm text-foreground">{children}</div>
    </div>
  );
}

function Tags({ items }: { items: string[] }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((t) => (
        <span
          key={t}
          className="rounded-full border border-purple/25 bg-purple/[0.10] px-2 py-0.5 text-xs text-purple"
        >
          #{t}
        </span>
      ))}
    </div>
  );
}

// ---- per-type bodies ----

function VideoIdeas({ ideas }: { ideas: Ideas }): JSX.Element {
  const headlines = (ideas.headlines as string[]) ?? [];
  const tags = (ideas.tags as string[]) ?? [];
  const month = ideas.best_publish_month as number | undefined;
  return (
    <div className="space-y-3">
      <Row label="Заголовки">
        <ul className="list-disc pl-5">
          {headlines.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ul>
      </Row>
      <Row label="Хук (3 сек)">{String(ideas.hook ?? "")}</Row>
      <Row label="Теги">
        <Tags items={tags} />
      </Row>
      {month && (
        <Row label="Лучший месяц">{MONTHS[month] ?? String(month)}</Row>
      )}
    </div>
  );
}

function PostsIdeas({ ideas }: { ideas: Ideas }): JSX.Element {
  const angles = (ideas.angles as string[]) ?? [];
  const hashtags = (ideas.hashtags as string[]) ?? [];
  return (
    <div className="space-y-3">
      <Row label="Заголовок">
        <b>{String(ideas.title ?? "")}</b>
      </Row>
      <Row label="Идеи раскрытия">
        <ul className="list-disc pl-5">
          {angles.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </Row>
      <Row label="Формат">{String(ideas.format ?? "")}</Row>
      <Row label="CTA">{String(ideas.cta ?? "")}</Row>
      <Row label="Хэштеги">
        <Tags items={hashtags} />
      </Row>
    </div>
  );
}

function LeadIdeas({ ideas }: { ideas: Ideas }): JSX.Element {
  const structure = (ideas.structure as string[]) ?? [];
  return (
    <div className="space-y-3">
      <Row label="Название">
        <b>{String(ideas.name ?? "")}</b>
      </Row>
      <Row label="Обещание">{String(ideas.promise ?? "")}</Row>
      <Row label="Структура">
        <ol className="list-decimal pl-5">
          {structure.map((s, i) => (
            <li key={`${i}-${s}`}>{s}</li>
          ))}
        </ol>
      </Row>
      <Row label="Формат">{String(ideas.format ?? "")}</Row>
    </div>
  );
}

function FullIdeas({ ideas }: { ideas: Ideas }): JSX.Element {
  const faq = (ideas.faq as { q: string; a: string }[]) ?? [];
  const links = (ideas.internal_links as string[]) ?? [];
  return (
    <div className="space-y-3">
      <Row label="H1">
        <b>{String(ideas.h1 ?? "")}</b>
      </Row>
      <Row label="Title SEO">{String(ideas.title_seo ?? "")}</Row>
      <Row label="Description">{String(ideas.description ?? "")}</Row>
      <Row label="FAQ">
        <ul className="space-y-1.5">
          {faq.map((f, i) => (
            <li key={`${i}-${f.q}`}>
              <b>{f.q}</b> — {f.a}
            </li>
          ))}
        </ul>
      </Row>
      <Row label="Внутр. ссылки">
        <Tags items={links} />
      </Row>
    </div>
  );
}
